/**
 * Cloudflare DNS management API service
 *
 * Provides methods for managing Cloudflare configurations and DNS records.
 * All configurations are stored securely with encrypted API tokens.
 */

import { apiClient } from './api'

// ===== Types =====

export interface CloudflareConfig {
  id: string // UUID
  name: string
  account_id: string | null
  default_zone_id: string | null
  is_active: boolean
  created_at: string
  updated_at: string
  created_by_id: string // UUID
}

export interface CloudflareConfigCreate {
  name: string
  api_token: string
  account_id?: string
  default_zone_id?: string
}

export interface CloudflareConfigUpdate {
  name?: string
  api_token?: string
  account_id?: string
  default_zone_id?: string
  is_active?: boolean
}

export interface CloudflareConfigListResponse {
  configs: CloudflareConfig[]
  total: number
}

export interface DNSZone {
  id: string
  name: string
  status: string
  name_servers: string[]
  paused: boolean
}

export interface DNSZoneListResponse {
  zones: DNSZone[]
  total: number
}

/**
 * As duas fontes do domínio-base da plataforma, lado a lado (#991).
 *
 * A fonte PRIMÁRIA é o `dns_zone_name` do frontend application `ec` — que não
 * tem tela nenhuma. Sem mostrá-la, o operador configura a Cloudflare enquanto o
 * outro lado decide, e a divergência entre as duas só aparece como "login
 * recusado".
 */
export interface PlatformDomainSources {
  ec_app_zone: string | null
  ec_app_found: boolean
  ec_app_error: string | null
  cloudflare_zone: string | null
  cloudflare_config_id: string | null
  cloudflare_config_name: string | null
  cloudflare_default_zone_id: string | null
  cloudflare_error: string | null
  /** Mais de uma config ativa: a plataforma lê a mais antiga (#964). */
  active_configs: number
  /** As duas fontes responderam zonas DIFERENTES — o domínio-base fica indefinido. */
  diverging: boolean
}

/**
 * O efeito de adotar uma zona como domínio-base.
 *
 * `base_domain` é o domínio que passou a valer DE FATO: gravar a zona com
 * sucesso nunca significou que ela resolveu (o app `ec` pode declarar outra, ou
 * a config pode não ser a ativa). É por isso que a tela mostra este campo, e não
 * apenas "salvo".
 */
export interface PlatformDomainChangeResult {
  base_domain: string | null
  base_domain_resolved: boolean
  base_domain_source: string | null
  base_domain_error: string | null
  invalidation_published: boolean
  sources: PlatformDomainSources
  message: string
  warnings: string[]
}

export interface DefaultZoneResult extends PlatformDomainChangeResult {
  config_id: string
  default_zone_id: string | null
  default_zone_name: string | null
}

/** A zona declarada pela aplicação `ec` — a fonte PRIMÁRIA do domínio-base. */
export interface EcAppZoneResult extends PlatformDomainChangeResult {
  app_id: string
  dns_zone_id: string | null
  dns_zone_name: string | null
}

export interface DNSRecord {
  id: string
  type: string
  name: string
  content: string
  ttl: number
  proxied: boolean
  created_on: string | null
  modified_on: string | null
  priority: number | null
  comment: string | null
}

export interface DNSRecordCreate {
  type: string
  name: string
  content: string
  ttl?: number
  proxied?: boolean
  priority?: number
  comment?: string
}

export interface DNSRecordUpdate {
  type?: string
  name?: string
  content?: string
  ttl?: number
  proxied?: boolean
  priority?: number
  comment?: string
}

export interface DNSRecordListResponse {
  records: DNSRecord[]
  total: number
  zone_id: string
  zone_name: string | null
}

export interface ConnectionTestResponse {
  success: boolean
  message: string
  account_email?: string
  account_id?: string
  zones_count?: number
}

// ===== Cloudflare Pages: descoberta e diagnóstico de projetos (#996, etapa 2) =====

/** Uma implantação do projeto, como a API da Cloudflare a descreve. */
export interface PagesDeploymentInfo {
  id: string | null
  short_id: string | null
  url: string | null
  environment: string | null
  created_on: string | null
  modified_on: string | null
  stage: string | null
  stage_status: string | null
  branch: string | null
  commit_hash: string | null
  trigger: string | null
}

/** Um projeto Pages da conta — o que a lista mostra, sem ler manifesto. */
export interface PagesProjectInfo {
  name: string
  id: string | null
  subdomain: string | null
  canonical_url: string | null
  domains: string[]
  production_branch: string | null
  created_on: string | null
  source_repo: string | null
  /** O deploy que os DOMÍNIOS do projeto servem hoje — "commit implantado" é este. */
  canonical_deployment: PagesDeploymentInfo | null
  /** O deploy mais recente — pode ser um preview de branch. */
  latest_deployment: PagesDeploymentInfo | null
}

/**
 * A lista, e DE QUAL conta ela veio. Com mais de uma config ativa (#964) "a
 * ativa" é a mais antiga (#991) — o operador precisa saber de qual conta são
 * os projetos que está vendo.
 */
export interface PagesProjectListResponse {
  projects: PagesProjectInfo[]
  total: number
  config_id: string
  config_name: string
  account_id: string
}

/**
 * Os QUATRO desfechos da leitura do `deployment-manifest.json`. Nunca colapsados:
 * `absent` é o estado de toda a frota até os projetos serem rebuildados com o
 * contrato; `unverified` (timeout, DNS, 5xx) NÃO é `absent`.
 */
export type ManifestOutcome = 'absent' | 'valid' | 'invalid' | 'unverified'

export interface DeploymentManifestInfo {
  application: string
  environment: string
  api_base_url: string
  commit_sha: string
}

export interface ManifestReading {
  outcome: ManifestOutcome
  /** `null` quando não houve endereço a ler (subdomínio fora de `*.pages.dev`). */
  url: string | null
  http_status: number | null
  content_type: string | null
  manifest: DeploymentManifestInfo | null
  problems: string[]
  warnings: string[]
  detail: string
}

export type PagesProjectVerdict = 'approved' | 'rejected' | 'unverified' | 'missing'

/**
 * O diagnóstico de um projeto. Os eixos `environment_matches`/`api_matches` são
 * `null` quando a comparação não aconteceu (sem manifesto, ou backend sem
 * declaração) — nunca `false` sem prova.
 */
export interface PagesProjectDiagnosis {
  project_name: string
  config_id: string
  config_name: string
  account_id: string
  /** `true` | `false` (404 para esta credencial) | `null` (a Cloudflare não respondeu) */
  exists: boolean | null
  exists_detail: string
  subdomain: string | null
  canonical_url: string | null
  domains: string[]
  production_branch: string | null
  source_repo: string | null
  canonical_deployment: PagesDeploymentInfo | null
  latest_deployment: PagesDeploymentInfo | null
  /** `null` quando o manifesto nem foi lido (projeto inexistente/não conferido). */
  manifest: ManifestReading | null
  backend_environment: string | null
  backend_api_url: string | null
  backend_declaration_problems: string[]
  environment_matches: boolean | null
  api_matches: boolean | null
  /** Informativo: o commit do manifesto é o que a Cloudflare diz servir em produção? */
  commit_matches: boolean | null
  verdict: PagesProjectVerdict
  verdict_code: string
  verdict_message: string
  retryable: boolean
}

// ===== Cloudflare Pages: implantações e domínios pelo vínculo (#996, etapas 4-5) =====

/** Estados da implantação: `inactive` é desativação administrativa, não resultado de verificação. */
export type FrontendDeploymentStatus = 'unverified' | 'valid' | 'invalid' | 'inactive'

/**
 * Uma implantação: a aplicação num ambiente, servida por um projeto Pages de uma
 * conexão. `last_manifest_commit` é o commit do manifesto lido na ÚLTIMA verificação —
 * `null` = nunca lido (a tela mostra que falta, não deduz do deploy da Cloudflare).
 */
export interface FrontendDeploymentInfo {
  id: string
  frontend_application_id: string
  application: string | null
  environment: string
  provider: string
  cloudflare_config_id: string
  config_name: string | null
  account_id: string | null
  provider_project_name: string
  canonical_url: string | null
  expected_api_url: string
  status: FrontendDeploymentStatus
  is_default: boolean
  last_verified_at: string | null
  last_error: string | null
  last_manifest_commit: string | null
  /** Worker de repasse do domínio próprio (#1173); `null` = Cloudflare for SaaS não configurado. */
  saas_worker_script?: string | null
  /** Domínios que PRENDEM a implantação: vinculados a ela OU a caminho dela (alvo de migração). */
  domains_count: number
  created_at: string
}

export interface FrontendDeploymentListResponse {
  deployments: FrontendDeploymentInfo[]
  total: number
  backend_environment: string | null
}

export interface FrontendDeploymentCreate {
  config_id: string
  project_name: string
  application: string
  set_default?: boolean
}

export interface FrontendDeploymentVerification {
  deployment: FrontendDeploymentInfo
  diagnosis: PagesProjectDiagnosis
}

/**
 * Os estados do ciclo de vida do domínio (máquina de estados da etapa 3) mais os três de
 * proveniência do backfill. `null` é o LEGADO: nenhuma rodada o classificou. Nenhum
 * destes se colapsa noutro na tela.
 */
export type PortalDomainProvisioningStatus =
  | 'draft'
  | 'validating'
  | 'provisioning'
  | 'active'
  | 'failed'
  | 'migrating'
  | 'deleting'
  | 'deleted'
  | 'cleanup_failed'
  | 'unresolved'
  | 'conflict'
  | 'invalid'

/** CORS em TRÊS respostas: Redis fora não é "bloqueado", é "não sei". */
export type PortalDomainCors = 'allowed' | 'blocked' | 'unknown'

/** A visão completa por domínio: o que o vínculo não resolve vem `null`. */
export interface PortalDomainAdminInfo {
  id: string
  domain: string
  /** `proprio` = domínio da revenda via Cloudflare for SaaS (#1173); ausente/`plataforma` = subdomínio da zona. */
  kind?: 'plataforma' | 'proprio'
  /** Só `proprio`: o alvo do CNAME do cliente (subdomínio de plataforma ativo da revenda). */
  cname_target?: string | null
  worker_route_id?: string | null
  reseller_id: string | null
  reseller_name: string | null
  zone_name: string | null
  zone_id: string | null
  frontend_deployment_id: string | null
  cloudflare_config_id: string | null
  config_name: string | null
  provider_project_name: string | null
  environment: string | null
  expected_api_url: string | null
  provider_domain_id: string | null
  dns_record_id: string | null
  dns_status: string | null
  ssl_status: string | null
  verification_status: string | null
  cors: PortalDomainCors
  provisioning_status: PortalDomainProvisioningStatus | null
  migration_target_deployment_id: string | null
  migration_target_project_name: string | null
  last_checked_at: string | null
  last_provisioning_error: string | null
  provisioned_at: string | null
  /** Hostname da plataforma (portal ou projeto): protegido contra remoção. */
  is_platform_canonical: boolean
  /** Primário da revenda; durante uma troca o antigo segue primário até sair (#1144). */
  is_primary: boolean
  created_at: string
}

/** Instrução devolvida pelo ciclo (a mesma que o RS recebe): SUCCESS | INFO | WARNING. */
export interface PortalDomainInstruction {
  type: 'SUCCESS' | 'INFO' | 'WARNING' | 'CNAME' | string
  code?: string
  /** Só `CNAME` (#1173): o nome e o alvo do registro que o cliente cria. */
  name?: string
  value?: string | null
  message: string
  description?: string
}

/** Cadastro pelo ADM em nome da revenda (#1144). */
export interface PortalDomainAdminCreateResponse {
  domain: PortalDomainAdminInfo
  dns_instructions: PortalDomainInstruction[]
}

/**
 * Desfechos da TROCA (#1144), nenhum se colapsa: `replaced` (novo ativo, antigo removido),
 * `new_not_active` (o antigo segue servindo; chamar de novo retoma) e `old_not_removed`
 * (novo ativo, limpeza do antigo falhou — remover de novo é o retry).
 */
export type PortalDomainReplaceOutcome = 'replaced' | 'new_not_active' | 'old_not_removed'

export interface PortalDomainReplaceResponse {
  outcome: PortalDomainReplaceOutcome
  domain: PortalDomainAdminInfo
  removed_domain: string | null
  dns_instructions: PortalDomainInstruction[]
}

export interface PortalDomainAdminListResponse {
  domains: PortalDomainAdminInfo[]
  total: number
  backend_environment: string | null
}

/** Um evento do rastro: quem, quando, e em `details` o projeto anterior/novo e o resultado. */
export interface PortalDomainEvent {
  id: string
  action: string
  message: string
  user_id: string | null
  details: Record<string, unknown>
  created_at: string
}

export interface PortalDomainAdminDetail {
  domain: PortalDomainAdminInfo
  history: PortalDomainEvent[]
}

/** Os desfechos da reconciliação — "indeterminado" NUNCA é "ok" nem "divergente". */
export type PortalDomainReconcileOutcome =
  | 'ok'
  | 'divergente'
  | 'indeterminado'
  | 'corrigido'
  | 'sem_vinculo'
  | 'em_andamento'

export interface PortalDomainReconcileResponse {
  domain: string
  outcome: PortalDomainReconcileOutcome
  detail: string
  provisioning_status: string | null
  frontend_deployment_id: string | null
}

export interface PortalDomainMigrateResponse {
  domain: string
  completed: boolean
  from_project: string
  to_project: string
  preflight: PagesProjectDiagnosis
  provisioning_status: string | null
  frontend_deployment_id: string | null
}

export interface PagesProjectValidation {
  valid: boolean
  diagnosis: PagesProjectDiagnosis
}

// ===== API Functions =====

const cloudflareApi = {
  // ===== Configuration Management =====

  /**
   * Create a new Cloudflare configuration
   */
  createConfig: (data: CloudflareConfigCreate): Promise<CloudflareConfig> => {
    return apiClient.post('/api/v1/cloudflare/configs', data)
  },

  /**
   * List all Cloudflare configurations
   */
  getConfigs: (): Promise<CloudflareConfigListResponse> => {
    return apiClient.get('/api/v1/cloudflare/configs')
  },

  /**
   * Get a specific Cloudflare configuration
   */
  getConfig: (id: string): Promise<CloudflareConfig> => {
    return apiClient.get(`/api/v1/cloudflare/configs/${id}`)
  },

  /**
   * Update a Cloudflare configuration
   */
  updateConfig: (id: string, data: CloudflareConfigUpdate): Promise<CloudflareConfig> => {
    return apiClient.put(`/api/v1/cloudflare/configs/${id}`, data)
  },

  /**
   * Delete a Cloudflare configuration
   */
  deleteConfig: (id: string): Promise<void> => {
    return apiClient.delete(`/api/v1/cloudflare/configs/${id}`)
  },

  /**
   * Adota uma zona como domínio-base da plataforma (#991).
   *
   * Operação PRÓPRIA, e não um campo do update de credencial: é esta zona que
   * decide o CORS, o escopo de login e a raiz dos subdomínios de revenda. Passar
   * `null` remove a zona padrão.
   */
  setDefaultZone: (configId: string, zoneId: string | null): Promise<DefaultZoneResult> => {
    return apiClient.put(`/api/v1/cloudflare/configs/${configId}/default-zone`, {
      zone_id: zoneId,
    })
  },

  /**
   * Declara a zona da aplicação `ec` — a fonte PRIMÁRIA do domínio-base (#991).
   *
   * Adota a zona e NADA mais: não cria nem altera registro DNS (isso é
   * `configure_dns`, outra operação). `null` limpa a declaração e devolve a
   * decisão à configuração Cloudflare.
   */
  setEcAppZone: (zoneId: string | null): Promise<EcAppZoneResult> => {
    return apiClient.put('/api/v1/cloudflare/platform-domain/ec-app-zone', {
      zone_id: zoneId,
    })
  },

  /**
   * As duas fontes do domínio-base e o estado de divergência.
   */
  getPlatformDomainSources: (): Promise<PlatformDomainSources> => {
    return apiClient.get('/api/v1/cloudflare/platform-domain')
  },

  /**
   * Test connection to Cloudflare API using saved config
   */
  testConnection: (id: string): Promise<ConnectionTestResponse> => {
    return apiClient.post(`/api/v1/cloudflare/configs/${id}/test`)
  },

  /**
   * Test a Cloudflare API token without saving it
   */
  testToken: (apiToken: string): Promise<ConnectionTestResponse> => {
    return apiClient.post('/api/v1/cloudflare/test-token', { api_token: apiToken })
  },

  // ===== DNS Zone Management =====

  /**
   * List all DNS zones accessible with the configuration
   */
  getZones: (configId?: string): Promise<DNSZoneListResponse> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.get(`/api/v1/cloudflare/zones${params}`)
  },

  /**
   * List DNS records for a specific zone
   */
  getDNSRecords: (
    zoneId: string,
    options?: {
      configId?: string
      recordType?: string
      name?: string
    },
  ): Promise<DNSRecordListResponse> => {
    const params = new URLSearchParams()
    if (options?.configId) params.append('config_id', options.configId)
    if (options?.recordType) params.append('record_type', options.recordType)
    if (options?.name) params.append('name', options.name)

    const queryString = params.toString()
    return apiClient.get(
      `/api/v1/cloudflare/zones/${zoneId}/dns${queryString ? `?${queryString}` : ''}`,
    )
  },

  /**
   * Create a new DNS record in a zone
   */
  createDNSRecord: (
    zoneId: string,
    data: DNSRecordCreate,
    configId?: string,
  ): Promise<DNSRecord> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.post(`/api/v1/cloudflare/zones/${zoneId}/dns${params}`, data)
  },

  /**
   * Update an existing DNS record
   */
  updateDNSRecord: (
    zoneId: string,
    recordId: string,
    data: DNSRecordUpdate,
    configId?: string,
  ): Promise<DNSRecord> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.put(`/api/v1/cloudflare/zones/${zoneId}/dns/${recordId}${params}`, data)
  },

  /**
   * Delete a DNS record
   */
  deleteDNSRecord: (zoneId: string, recordId: string, configId?: string): Promise<void> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.delete(`/api/v1/cloudflare/zones/${zoneId}/dns/${recordId}${params}`)
  },

  // ===== Cloudflare Pages: projetos (#996, etapa 2) =====

  /**
   * Os projetos Pages da conta, pela API oficial. É desta lista que um projeto
   * passa a ser ESCOLHIDO (etapas seguintes), nunca digitado.
   */
  getPagesProjects: (configId?: string): Promise<PagesProjectListResponse> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.get(`/api/v1/cloudflare/pages/projects${params}`)
  },

  /**
   * O diagnóstico de um projeto: existência, conta, o que a Cloudflare sabe, o
   * que o manifesto diz e se corresponde a este backend. Sempre 200 — os
   * desfechos vão no corpo.
   */
  getPagesProjectDiagnosis: (
    projectName: string,
    configId?: string,
  ): Promise<PagesProjectDiagnosis> => {
    const params = configId ? `?config_id=${configId}` : ''
    // `getLong` (120s): o backend faz DOIS I/Os externos em série — a Cloudflare
    // (até 30s) e o subdomínio do projeto (até 8s). O timeout padrão do
    // `apiClient` (30s) abortaria a request ANTES do `unverified` que o backend
    // ia responder, e a tela mostraria "timeout" no lugar do diagnóstico.
    return apiClient.getLong(
      `/api/v1/cloudflare/pages/projects/${encodeURIComponent(projectName)}/diagnosis${params}`,
    )
  },

  /**
   * A RECUSA: 200 só quando o projeto está aprovado para este ambiente. A
   * recusa vem como erro HTTP com `error.code`, `error.retryable` e
   * `error.diagnosis` — ler por `getApiErrorCode`/`getErrorMessage`.
   */
  validatePagesProject: (
    projectName: string,
    configId?: string,
  ): Promise<PagesProjectValidation> => {
    const params = configId ? `?config_id=${configId}` : ''
    // Mesmo orçamento do diagnóstico (ver acima).
    return apiClient.postLong(
      `/api/v1/cloudflare/pages/projects/${encodeURIComponent(projectName)}/validate${params}`,
    )
  },

  // ===== Cloudflare Pages: implantações (#996, etapas 4-5) =====

  getPagesDeployments: (): Promise<FrontendDeploymentListResponse> =>
    apiClient.get('/api/v1/cloudflare/pages/deployments'),

  /** Cria a implantação a partir de um projeto ESCOLHIDO da lista e aprovado pelo manifesto. */
  createPagesDeployment: (
    data: FrontendDeploymentCreate,
  ): Promise<FrontendDeploymentVerification> =>
    apiClient.postLong('/api/v1/cloudflare/pages/deployments', data),

  /** "Testar": relê o manifesto e grava status/erro/commit — sem mudar o par nem o padrão. */
  verifyPagesDeployment: (id: string): Promise<FrontendDeploymentVerification> =>
    apiClient.postLong(`/api/v1/cloudflare/pages/deployments/${id}/verify`),

  setDefaultPagesDeployment: (id: string): Promise<FrontendDeploymentInfo> =>
    apiClient.put(`/api/v1/cloudflare/pages/deployments/${id}/default`),

  /** Desativar (`inactive`) ou reativar (`unverified` — o verify decide). A padrão não se desativa. */
  setPagesDeploymentStatus: (id: string, active: boolean): Promise<FrontendDeploymentInfo> =>
    apiClient.put(`/api/v1/cloudflare/pages/deployments/${id}/status`, { active }),

  /** Só sem domínios (409 com qualquer um vinculado ou a caminho). */
  deletePagesDeployment: (id: string): Promise<void> =>
    apiClient.delete(`/api/v1/cloudflare/pages/deployments/${id}`),

  // ===== Cloudflare Pages: domínios de portal, a visão do Admin (#996, etapa 5) =====

  /** Com `resellerId`, só os domínios da revenda (#1144): a mesma consulta da seção da org e da tela. */
  getPagesDomains: (resellerId?: string): Promise<PortalDomainAdminListResponse> =>
    apiClient.get(
      '/api/v1/cloudflare/pages/domains',
      resellerId ? { params: { reseller_id: resellerId } } : undefined,
    ),

  /** O ADM cadastra o subdomínio da revenda PELO MESMO ciclo do RS (#1144). */
  createResellerPagesDomain: (
    resellerId: string,
    subdomain: string,
  ): Promise<PortalDomainAdminCreateResponse> =>
    apiClient.postWorkflow(`/api/v1/cloudflare/pages/resellers/${resellerId}/domains`, {
      subdomain,
    }),

  /** Domínio PRÓPRIO da revenda (#1173, só superadmin): Custom Hostname + rota do Worker; nasce pendente. */
  createResellerOwnDomain: (
    resellerId: string,
    host: string,
  ): Promise<PortalDomainAdminCreateResponse> =>
    apiClient.postWorkflow(`/api/v1/cloudflare/pages/resellers/${resellerId}/own-domain`, { host }),

  /** Grava (ou limpa com `null`) o script do Worker de repasse da implantação (#1173). */
  setDeploymentSaasWorker: (id: string, script: string | null): Promise<FrontendDeploymentInfo> =>
    apiClient.put(`/api/v1/cloudflare/pages/deployments/${id}/saas-worker`, { script }),

  /**
   * TROCA o subdomínio (#1144): cadastra o novo, espera ficar ativo e só então remove o
   * antigo. Orçamento de WORKFLOW: preflight + escrita no provedor + remoção do antigo.
   */
  replacePagesDomain: (id: string, subdomain: string): Promise<PortalDomainReplaceResponse> =>
    apiClient.postWorkflow(`/api/v1/cloudflare/pages/domains/${id}/replace`, { subdomain }),

  getPagesDomain: (id: string): Promise<PortalDomainAdminDetail> =>
    apiClient.get(`/api/v1/cloudflare/pages/domains/${id}`),

  /** `fix=false` só compara (validar); `fix=true` reprovisiona no projeto DO VÍNCULO se divergente. */
  reconcilePagesDomain: (id: string, fix: boolean): Promise<PortalDomainReconcileResponse> =>
    apiClient.postLong(`/api/v1/cloudflare/pages/domains/${id}/reconcile`, { fix }),

  /**
   * `confirm=false` = preflight do destino (diagnóstico); `confirm=true` = migra.
   * Orçamento de WORKFLOW (5 min): preflight (projeto, manifesto, zonas) + até quatro
   * escritas no provedor, cada uma com até 30 s — um timeout do cliente no meio diria
   * "abortado" para uma migração que o servidor pode ter continuado.
   */
  migratePagesDomain: (
    id: string,
    targetDeploymentId: string,
    confirm: boolean,
  ): Promise<PortalDomainMigrateResponse> =>
    apiClient.postWorkflow(`/api/v1/cloudflare/pages/domains/${id}/migrate`, {
      target_deployment_id: targetDeploymentId,
      confirm,
    }),

  /** Remoção PELO CICLO: limpa o provedor no projeto do vínculo; `cleanup_failed` mantém a linha (chamar de novo é o retry). */
  deletePagesDomain: (id: string): Promise<void> =>
    // Até 4 chamadas à Cloudflare antes de responder: o mesmo orçamento de reconcile/migrate.
    apiClient.deleteLong(`/api/v1/cloudflare/pages/domains/${id}`),

  /** Só para quem NÃO tem vínculo resolvido: apaga a linha SEM tocar o provedor, com motivo no rastro. */
  forceDeletePagesDomain: (id: string, reason: string): Promise<void> =>
    apiClient.post(`/api/v1/cloudflare/pages/domains/${id}/force-delete`, {
      confirm: true,
      reason,
    }),
}

// ===== Helper Functions =====

/**
 * Get status color for DNS zone status
 */
export const getZoneStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100'
    case 'pending':
      return 'text-yellow-600 bg-yellow-100'
    case 'initializing':
      return 'text-blue-600 bg-blue-100'
    case 'moved':
      return 'text-orange-600 bg-orange-100'
    case 'deleted':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

/**
 * Get status label for DNS zone status
 */
export const getZoneStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Ativo'
    case 'pending':
      return 'Pendente'
    case 'initializing':
      return 'Inicializando'
    case 'moved':
      return 'Movido'
    case 'deleted':
      return 'Deletado'
    default:
      return status
  }
}

/**
 * Get color for DNS record type
 */
export const getRecordTypeColor = (type: string): string => {
  switch (type) {
    case 'A':
      return 'text-blue-600 bg-blue-100'
    case 'AAAA':
      return 'text-indigo-600 bg-indigo-100'
    case 'CNAME':
      return 'text-purple-600 bg-purple-100'
    case 'TXT':
      return 'text-green-600 bg-green-100'
    case 'MX':
      return 'text-orange-600 bg-orange-100'
    case 'NS':
      return 'text-yellow-600 bg-yellow-100'
    case 'SRV':
      return 'text-pink-600 bg-pink-100'
    case 'CAA':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

/**
 * Format TTL value to human-readable string
 */
export const formatTTL = (ttl: number): string => {
  if (ttl === 1) return 'Auto'
  if (ttl < 60) return `${ttl}s`
  if (ttl < 3600) return `${Math.floor(ttl / 60)}m`
  if (ttl < 86400) return `${Math.floor(ttl / 3600)}h`
  return `${Math.floor(ttl / 86400)}d`
}

/**
 * DNS record types available in Cloudflare
 */
export const DNS_RECORD_TYPES = [
  'A',
  'AAAA',
  'CNAME',
  'TXT',
  'MX',
  'NS',
  'SRV',
  'CAA',
  'HTTPS',
  'SVCB',
  'PTR',
  'LOC',
  'CERT',
  'DNSKEY',
  'DS',
  'NAPTR',
  'SMIMEA',
  'SSHFP',
  'TLSA',
  'URI',
]

export default cloudflareApi
